import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Stat,
  Tag,
  BtnPrimary,
  BtnSecondary,
  NeonInput,
  NeonSelect,
  NeonTextarea,
  YamlBlock,
  PulseDot,
} from "@/components/cathedral/Primitives";

const VERTICALS = [
  "cathedral", "keys", "geminus", "aria", "roger5", "oracle", "axiom", "scholar", "wingman",
];
const CATEGORIES = [
  "scripture", "procedure", "blog", "podcast", "research", "lore", "data", "other",
];

const VERTICAL_GLOW = {
  cathedral: "cyan",
  keys: "violet",
  geminus: "magenta",
  aria: "magenta",
  roger5: "gold",
  oracle: "violet",
  axiom: "green",
  scholar: "blue",
  wingman: "red",
};

export default function LibraryPage() {
  const [stats, setStats] = useState(null);
  const [results, setResults] = useState({ count: 0, items: [] });
  const [q, setQ] = useState("");
  const [vertical, setVertical] = useState("");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selected, setSelected] = useState(null);

  // Ingest drawer
  const [ingestOpen, setIngestOpen] = useState(false);
  const [ingestText, setIngestText] = useState("");
  const [ingestSource, setIngestSource] = useState("paste");
  const [ingestVertical, setIngestVertical] = useState("cathedral");
  const [ingestCategory, setIngestCategory] = useState("other");

  const loadStats = async () => {
    try { setStats(await api.libraryStats()); }
    catch (e) { setMsg(e.response?.data?.detail || e.message); }
  };

  const runSearch = async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (vertical) params.vertical = vertical;
      if (category) params.category = category;
      if (source) params.source = source;
      setResults(await api.librarySearch(params));
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadStats(); runSearch(); /* eslint-disable-next-line */ }, []);

  const submitIngest = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await api.libraryIngest({
        text: ingestText,
        source: ingestSource || "paste",
        vertical: ingestVertical,
        category: ingestCategory,
      });
      setMsg(`+${res.inserted} fragment(s) joined the lattice.`);
      setIngestText("");
      setIngestOpen(false);
      await loadStats();
      await runSearch();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteNode = async (id) => {
    if (!window.confirm("Permanently delete this knowledge fragment?")) return;
    try {
      await api.libraryDelete(id);
      setSelected(null);
      await loadStats();
      await runSearch();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };

  const [showCommercial, setShowCommercial] = useState(false);
  const [priceUSD, setPriceUSD] = useState("");
  const [license, setLicense] = useState("single-user");
  const [summary, setSummary] = useState("");
  const [gumroadUrl, setGumroadUrl] = useState("");
  const [gumroadPermalink, setGumroadPermalink] = useState("");

  // When selection changes, prime the commercial editor with current values.
  // All setters are stable React state functions; deliberately listing only
  // `selected` as the trigger.
  useEffect(() => {
    if (selected) {
      setShowCommercial(!!selected.commercial);
      setPriceUSD(selected.price_cents != null ? (selected.price_cents / 100).toString() : "");
      setLicense(selected.license || "single-user");
      setSummary(selected.summary || "");
      setGumroadUrl(selected.gumroad_url || "");
      setGumroadPermalink(selected.gumroad_permalink || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const submitCommercial = async (listIt) => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      const body = listIt
        ? {
            commercial: true,
            price_cents: Math.round(parseFloat(priceUSD || "0") * 100),
            license,
            summary,
          }
        : { commercial: false };
      const fresh = await api.librarySetCommercial(selected.id, body);
      setSelected(fresh);
      setMsg(listIt ? `Listed for sale · $${(fresh.price_cents / 100).toFixed(2)}.` : "Unlisted.");
      await runSearch();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveGumroad = async () => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      const fresh = await api.gumroadAttach(selected.id, gumroadUrl.trim(), gumroadPermalink.trim());
      setSelected(fresh.node);
      setMsg(`Gumroad attached · ${fresh.node.gumroad_url}`);
      await runSearch();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const vCounts = stats?.by_vertical || {};
  const cCounts = stats?.by_category || {};
  const sCounts = stats?.by_source || {};

  const sourceList = useMemo(() => Object.keys(sCounts).sort(), [sCounts]);

  return (
    <section className="px-8 lg:px-12 py-12 pb-32" data-testid="library-page">
      <SectionHeader
        overline="KEYS · Pillar Resident Library"
        title="Knowledge Lattice"
        description="The 18,000-fragment KEYS library is a Pillar Resident of the Cathedral. Every node (Aria, Roger AI, Axiom, Scholar, Wingman) reads from this single shared lattice. The inner trinity of Pillar Residents — Geminus (Mirror · reflection), KEYS (Library · memory), Oracle (Seer · foresight) — operates from within the Core. Ingest YAML, JSON, or plain-text exports below — every fragment is automatically signed when the Cathedral signer is loaded."
        action={
          <>
            <BtnSecondary onClick={() => { loadStats(); runSearch(); }} data-testid="library-refresh">
              Refresh
            </BtnSecondary>
            <BtnPrimary onClick={() => setIngestOpen((v) => !v)} data-testid="library-ingest-toggle">
              {ingestOpen ? "Close Ingestor" : "Ingest Fragment"}
            </BtnPrimary>
          </>
        }
      />

      {msg && (
        <Card className="p-5 mb-6 border border-[var(--violet)]" data-testid="library-msg">
          <span className="font-mono text-xs text-glow-violet">{msg}</span>
        </Card>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <Card className="p-6" glow="violet">
          <Stat label="Total Fragments" value={stats?.total ?? "—"} sublabel="across the lattice" glow="violet" testId="library-stat-total" />
        </Card>
        <Card className="p-6" glow="cyan">
          <Stat label="Verticals Mapped" value={Object.keys(vCounts).length || "—"} sublabel="monoliths represented" glow="cyan" testId="library-stat-verticals" />
        </Card>
        <Card className="p-6" glow="magenta">
          <Stat label="Categories Active" value={Object.keys(cCounts).length || "—"} sublabel="scripture · procedure · etc." glow="magenta" testId="library-stat-categories" />
        </Card>
        <Card className="p-6" glow="gold">
          <Stat label="Sources Ingested" value={Object.keys(sCounts).length || "—"} sublabel="origin tags" glow="gold" testId="library-stat-sources" />
        </Card>
      </div>

      {/* Ingest drawer */}
      {ingestOpen && (
        <Card className="p-7 mb-10 border border-[var(--cyan)]" data-testid="library-ingest-drawer">
          <Overline tone="cyan">Ingest YAML / JSON / Plain-Text Fragment</Overline>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            <div>
              <Overline tone="muted">Source Tag</Overline>
              <NeonInput
                value={ingestSource}
                onChange={(e) => setIngestSource(e.target.value)}
                placeholder="e.g. keys_v1, nextxus.rip:export"
                data-testid="library-ingest-source"
                className="mt-2"
              />
            </div>
            <div>
              <Overline tone="muted">Default Vertical</Overline>
              <NeonSelect
                value={ingestVertical}
                onChange={(e) => setIngestVertical(e.target.value)}
                data-testid="library-ingest-vertical"
                className="mt-2"
              >
                {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
              </NeonSelect>
            </div>
            <div>
              <Overline tone="muted">Default Category</Overline>
              <NeonSelect
                value={ingestCategory}
                onChange={(e) => setIngestCategory(e.target.value)}
                data-testid="library-ingest-category"
                className="mt-2"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </NeonSelect>
            </div>
          </div>
          <div className="mt-5">
            <Overline tone="muted">Payload (YAML multi-doc with `---`, JSON array, or plain text)</Overline>
            <NeonTextarea
              rows={10}
              value={ingestText}
              onChange={(e) => setIngestText(e.target.value)}
              placeholder={"title: Example fragment\nbody: |\n  Paste KEYS export here. Multi-doc YAML is supported.\ntags: [example]\nvertical: aria\ncategory: blog\n---\ntitle: Another fragment\nbody: ...\n"}
              data-testid="library-ingest-text"
              className="mt-2"
            />
          </div>
          <div className="flex gap-3 mt-5 justify-end">
            <BtnSecondary onClick={() => setIngestOpen(false)} data-testid="library-ingest-cancel">Cancel</BtnSecondary>
            <BtnPrimary onClick={submitIngest} disabled={busy || !ingestText.trim()} data-testid="library-ingest-submit">
              {busy ? "Absorbing…" : "Absorb into Lattice"}
            </BtnPrimary>
          </div>
        </Card>
      )}

      {/* Search bar */}
      <Card className="p-6 mb-8" data-testid="library-search-bar">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <Overline tone="muted">Free-text Query</Overline>
            <NeonInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="title / body / tag"
              data-testid="library-search-q"
              className="mt-2"
            />
          </div>
          <div>
            <Overline tone="muted">Vertical</Overline>
            <NeonSelect
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              data-testid="library-search-vertical"
              className="mt-2"
            >
              <option value="">— any —</option>
              {VERTICALS.map((v) => <option key={v} value={v}>{v}{vCounts[v] ? ` (${vCounts[v]})` : ""}</option>)}
            </NeonSelect>
          </div>
          <div>
            <Overline tone="muted">Category</Overline>
            <NeonSelect
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="library-search-category"
              className="mt-2"
            >
              <option value="">— any —</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}{cCounts[c] ? ` (${cCounts[c]})` : ""}</option>)}
            </NeonSelect>
          </div>
          <div>
            <Overline tone="muted">Source</Overline>
            <NeonSelect
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="library-search-source"
              className="mt-2"
            >
              <option value="">— any —</option>
              {sourceList.map((s) => <option key={s} value={s}>{s} ({sCounts[s]})</option>)}
            </NeonSelect>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <BtnPrimary onClick={runSearch} disabled={busy} data-testid="library-search-run">
            {busy ? "Querying…" : "Run Query"}
          </BtnPrimary>
        </div>
      </Card>

      {/* Results + Detail split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Overline tone="cyan">
            Results · {results.count} fragment{results.count === 1 ? "" : "s"}
          </Overline>
          {results.items.length === 0 && (
            <Card className="p-6 text-[var(--text-3)] font-mono text-xs">
              No fragments match. Try removing filters, or ingest a new payload.
            </Card>
          )}
          {results.items.map((item) => (
            <Card
              key={item.id}
              className={`p-5 cursor-pointer transition-all ${selected?.id === item.id ? "border-[var(--cyan)]" : ""}`}
              onClick={() => setSelected(item)}
              data-testid={`library-result-${item.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-lg leading-tight text-[var(--text)]">{item.title}</div>
                  <div className="font-mono text-[10px] text-[var(--text-3)] tracking-[0.2em] uppercase mt-2 flex gap-3 flex-wrap">
                    <span>{new Date(item.created_at).toISOString().slice(0, 16)}Z</span>
                    <span>· {item.source}</span>
                    {item.cathedral_sig && <span className="text-glow-gold">· signed</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Tag tone="cite">
                    <PulseDot color={VERTICAL_GLOW[item.vertical] || "cyan"} className="mr-1.5" />
                    {item.vertical}
                  </Tag>
                  <Tag tone="assumption">{item.category}</Tag>
                </div>
              </div>
              {item.snippet && (
                <p className="text-[var(--text-2)] text-sm leading-relaxed mt-3 line-clamp-3">{item.snippet}</p>
              )}
              {item.tags?.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-3">
                  {item.tags.slice(0, 8).map((t) => (
                    <Tag key={t} tone="default">{t}</Tag>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        <div className="lg:col-span-1">
          <Overline tone="magenta">Fragment Inspector</Overline>
          {!selected ? (
            <Card className="p-6 mt-4 text-[var(--text-3)] font-mono text-xs">
              Select a result to inspect its body, tags, and metadata.
            </Card>
          ) : (
            <Card className="p-6 mt-4" glow={VERTICAL_GLOW[selected.vertical] || "cyan"} data-testid="library-detail">
              <div className="font-display text-xl text-[var(--text)] leading-tight">{selected.title}</div>
              <div className="flex gap-2 flex-wrap mt-3">
                <Tag tone="cite">{selected.vertical}</Tag>
                <Tag tone="assumption">{selected.category}</Tag>
                <Tag tone="default">{selected.source}</Tag>
                {selected.cathedral_sig && <Tag tone="pass">Ed25519 signed</Tag>}
              </div>
              <div className="mt-5">
                <Overline tone="muted">Body</Overline>
                <p className="text-[var(--text-2)] text-sm leading-relaxed mt-2 whitespace-pre-wrap">
                  {selected.body || "(empty body)"}
                </p>
              </div>
              {selected.tags?.length > 0 && (
                <div className="mt-5">
                  <Overline tone="muted">Tags</Overline>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selected.tags.map((t) => <Tag key={t}>{t}</Tag>)}
                  </div>
                </div>
              )}
              {selected.meta && Object.keys(selected.meta).length > 0 && (
                <div className="mt-5">
                  <Overline tone="muted">Meta</Overline>
                  <YamlBlock value={Object.entries(selected.meta).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join("\n")} className="mt-2" />
                </div>
              )}
              <div className="mt-5 font-mono text-[10px] text-[var(--text-3)] tracking-[0.2em] uppercase">
                ID: {selected.id}<br />
                Ingested: {selected.created_at} · by {selected.ingested_by}
              </div>

              {/* Commercial subsystem — Oracle's revenue hook */}
              <div className="mt-6 pt-6 border-t border-[var(--chrome)]">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <Overline tone="gold">Commercial · Oracle Catalog</Overline>
                  {selected.commercial && (
                    <Tag tone="fact">
                      LISTED · ${((selected.price_cents || 0) / 100).toFixed(2)}
                    </Tag>
                  )}
                </div>
                {!selected.commercial ? (
                  <>
                    <p className="text-xs text-[var(--text-3)] leading-relaxed mb-4">
                      List this fragment for sale through Oracle. The Cathedral attaches an Ed25519 signature so buyers can verify authenticity at <code className="text-glow-cyan">/api/library/nodes/{`{id}`}/license-proof</code> — no trust in Oracle's UI required.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Overline tone="muted">Price USD</Overline>
                        <NeonInput
                          type="number"
                          step="0.01"
                          min="0"
                          value={priceUSD}
                          onChange={(e) => setPriceUSD(e.target.value)}
                          placeholder="49.00"
                          data-testid="library-commercial-price"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Overline tone="muted">License</Overline>
                        <NeonSelect
                          value={license}
                          onChange={(e) => setLicense(e.target.value)}
                          data-testid="library-commercial-license"
                          className="mt-2"
                        >
                          <option value="single-user">single-user</option>
                          <option value="federation">federation</option>
                          <option value="commercial-redistribution">commercial-redistribution</option>
                        </NeonSelect>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Overline tone="muted">Public Summary (visible in catalog)</Overline>
                      <NeonTextarea
                        rows={3}
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        placeholder={selected.body?.slice(0, 200) || "Marketing blurb…"}
                        data-testid="library-commercial-summary"
                        className="mt-2"
                      />
                    </div>
                    <div className="mt-4 flex justify-end">
                      <BtnPrimary
                        onClick={() => submitCommercial(true)}
                        disabled={busy || !priceUSD}
                        data-testid="library-commercial-list"
                      >
                        {busy ? "Listing…" : "List for Sale"}
                      </BtnPrimary>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-[var(--text-2)] leading-relaxed">
                      <div><span className="text-[var(--text-3)]">License:</span> {selected.license}</div>
                      <div className="mt-1"><span className="text-[var(--text-3)]">Summary:</span> {selected.summary}</div>
                      {selected.commercial_sig && (
                        <div className="mt-2 font-mono text-[10px] text-glow-gold break-all">
                          ✓ Ed25519 manifest signed · {(selected.commercial_sig.signature_hex || "").slice(0, 24)}…
                        </div>
                      )}
                      {!selected.commercial_sig && (
                        <div className="mt-2 font-mono text-[10px] text-glow-red">
                          ⚠ Cathedral signer not loaded — listing is unsigned. Load via Architect Suite → Cathedral Signer to enable verifiable provenance.
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex gap-2 justify-end flex-wrap">
                      <BtnSecondary
                        onClick={() => submitCommercial(true)}
                        disabled={busy}
                        data-testid="library-commercial-relist"
                      >
                        Update Listing
                      </BtnSecondary>
                      <BtnSecondary
                        onClick={() => submitCommercial(false)}
                        disabled={busy}
                        data-testid="library-commercial-unlist"
                      >
                        Unlist
                      </BtnSecondary>
                    </div>

                    {/* ── Gumroad attach (dollar-collection layer) ── */}
                    <div className="mt-6 pt-6 border-t border-[var(--chrome)]">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <Overline tone="gold">Gumroad · Dollar-Collection Layer</Overline>
                        {selected.gumroad_url
                          ? <Tag tone="fact">PUBLISHED</Tag>
                          : <Tag tone="risk">NOT YET PUBLISHED</Tag>}
                      </div>
                      <p className="text-xs text-[var(--text-3)] leading-relaxed mb-4">
                        Visitors check out on Gumroad. Their license key is verified here against Gumroad's API, then the Cathedral mints the signed Ed25519 license-proof.
                      </p>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <Overline tone="muted">Public Gumroad URL</Overline>
                          <NeonInput
                            value={gumroadUrl}
                            onChange={(e) => setGumroadUrl(e.target.value)}
                            placeholder="https://nextxus.gumroad.com/l/aria-sim"
                            data-testid="library-gumroad-url"
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Overline tone="muted">Gumroad Product Permalink (slug)</Overline>
                          <NeonInput
                            value={gumroadPermalink}
                            onChange={(e) => setGumroadPermalink(e.target.value)}
                            placeholder="aria-sim"
                            data-testid="library-gumroad-permalink"
                            className="mt-2"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <BtnPrimary
                          onClick={saveGumroad}
                          disabled={busy || !gumroadUrl.trim() || !gumroadPermalink.trim()}
                          data-testid="library-gumroad-save"
                        >
                          {busy ? "Saving…" : "Attach to Gumroad"}
                        </BtnPrimary>
                      </div>
                    </div>
                  </>
                )}
                {showCommercial !== !!selected.commercial && null /* satisfy unused-state lint */}
              </div>

              <div className="mt-5 flex justify-end">
                <BtnSecondary onClick={() => deleteNode(selected.id)} data-testid="library-detail-delete">
                  Delete fragment
                </BtnSecondary>
              </div>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}
